import MetricCard from "@/components/MetricCard";
import MetricsGrid from "@/components/MetricsGrid";
import {
  Briefcase,
  Sparkles,
  ArrowRight,
  TrendingUp,
  MapPin,
  Clock,
  CheckCircle2,
  Building2,
  ChevronRight,
  IndianRupee,
  LayoutGrid,
  FileText,
  Award,
  BellRing,
  CalendarCheck
} from "lucide-react";

import { useAuthStore } from "@/store/auth/authStore";
import { useActiveInternshipQuery, useInternshipsQuery } from "@/hooks/api/internship.queries";
import { Button, Spinner } from "@/components";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useDashboardAnalyticsQuery } from "@/hooks/api/analytics.queries";
import { formatDate } from "@/utils/formatDate.util";

const Dashboard = () => {
  const { user } = useAuthStore();
  const { data: activeInternship, isLoading: loadingActiveInternship } = useActiveInternshipQuery();
  const { data: internshipsData, isLoading: loadingInternships } = useInternshipsQuery({ limit: 6, status: "published" });
  const { data: analytics, isLoading: loadingAnalytics } = useDashboardAnalyticsQuery();

  const internships = internshipsData?.internships || [];

  const firstName = user?.name?.split(' ')[0] || "Student";

  const metrics = [
    {
      title: "Total Applications",
      value: loadingAnalytics ? "..." : (analytics?.total_applications || 0).toString(),
      iconRight: (
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600 shadow-inner">
          <FileText size={20} />
        </span>
      ),
      trend: { icon: <TrendingUp size={12} />, value: "Live", color: "indigo" },
      valueDescription: "Applications submitted",
      testId: "metric-total-applications",
      href: "/applications",
      className: "bg-white/60 backdrop-blur-xl border-white/40 shadow-xl shadow-slate-200/40",
    },
    {
      title: "Upcoming Interviews",
      value: loadingAnalytics ? "..." : (analytics?.upcoming_interviews || 0).toString(),
      iconRight: (
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-violet-100 text-violet-600 shadow-inner">
          <CalendarCheck size={20} />
        </span>
      ),
      trend: { icon: <Clock size={12} />, value: "Scheduled", color: "violet" },
      valueDescription: `${loadingAnalytics ? "..." : analytics?.total_interviews || 0} interviews in total`,
      testId: "metric-upcoming-interviews",
      href: "/interviews",
      className: "bg-white/60 backdrop-blur-xl border-white/40 shadow-xl shadow-slate-200/40",
    },
    {
      title: "Total Offers",
      value: loadingAnalytics ? "..." : (analytics?.total_offers || 0).toString(),
      iconRight: (
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 shadow-inner">
          <Award size={20} />
        </span>
      ),
      trend: { icon: <TrendingUp size={12} />, value: "Career", color: "emerald" },
      valueDescription: "Offers received",
      testId: "metric-total-offers",
      href: "/offers",
      className: "bg-white/60 backdrop-blur-xl border-white/40 shadow-xl shadow-slate-200/40",
    },
    {
      title: "Pending Offers",
      value: loadingAnalytics ? "..." : (analytics?.pending_offers || 0).toString(),
      iconRight: (
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 shadow-inner">
          <Clock size={20} />
        </span>
      ),
      trend: { icon: <BellRing size={12} />, value: "Action", color: "amber" },
      valueDescription: "Awaiting response",
      testId: "metric-pending-offers",
      href: "/offers",
      className: "bg-white/60 backdrop-blur-xl border-white/40 shadow-xl shadow-slate-200/40",
    },
    {
      title: "Accepted Offers",
      value: loadingAnalytics ? "..." : (analytics?.accepted_offers || 0).toString(),
      iconRight: (
        <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100 text-blue-600 shadow-inner">
          <CheckCircle2 size={20} />
        </span>
      ),
      trend: { icon: <Sparkles size={12} />, value: "Goal", color: "blue" },
      valueDescription: "Confirmed placements",
      testId: "metric-accepted-offers",
      href: "/offers",
      className: "bg-white/60 backdrop-blur-xl border-white/40 shadow-xl shadow-slate-200/40",
    },
  ];

  return (
    <div className="relative flex flex-col gap-6 md:gap-8 p-4 md:p-8 min-h-screen">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 h-[400px] w-[400px] bg-violet-500/10 blur-[100px] rounded-full pointer-events-none" />

      {/* --- NEW MOBILE HERO SECTION --- */}
      <div className="block md:hidden w-full space-y-6 pt-2 pb-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* Header Text & Badges */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-indigo-100 text-indigo-700 border-none px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
              Student Portal
            </Badge>
            {activeInternship && (
              <Badge className="bg-emerald-100 text-emerald-700 border-none px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Active
              </Badge>
            )}
          </div>

          <div className="space-y-1.5">
            <h1 className="text-4xl font-black text-slate-800 tracking-tight leading-[1.1]">
              Welcome back, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">{firstName}!</span>
            </h1>
            <p className="text-slate-500 text-sm font-medium leading-relaxed max-w-[90%]">
              {activeInternship?.has_active_internship
                ? `You are doing great as a ${activeInternship?.offer?.internship_title}. Keep up the high standards!`
                : "Start exploring tailored internship opportunities and launch your career journey today."}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-row items-center gap-3 w-full">
          <Link to="/discover/internships" className="flex-1">
            <Button className="w-full bg-indigo-600 text-white hover:bg-indigo-700 py-6 rounded-[1.2rem] font-black shadow-lg shadow-indigo-200 transition-all active:scale-95 text-[13px]">
              {activeInternship?.has_active_internship ? "Explore" : "Find My Future"}
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
          </Link>
          <Link to="/profile" className="flex-none">
            <Button variant="outline" className="w-full bg-white text-slate-700 hover:bg-slate-50 py-6 px-6 rounded-[1.2rem] font-bold border-slate-200 shadow-sm transition-all active:scale-95 text-[13px]">
              Profile
            </Button>
          </Link>
        </div>

        {/* Status Widget */}
        <div className="pt-2 w-full">
          {loadingActiveInternship ? (
            <div className="bg-white rounded-[1.5rem] p-4 border border-slate-100 shadow-sm flex items-center gap-4">
              <Spinner className="text-indigo-600 w-5 h-5" />
              <p className="text-slate-500 font-bold text-xs">Syncing career logs...</p>
            </div>
          ) : activeInternship?.has_active_internship ? (
            <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-[1.5rem] p-[2px] shadow-sm overflow-hidden border border-indigo-100/50">
              <div className="bg-white/60 backdrop-blur-md rounded-[1.4rem] p-4 flex flex-row items-center gap-4">
                 <div className="w-12 h-12 rounded-[1rem] bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-indigo-50">
                   <Briefcase className="text-indigo-600 w-6 h-6" />
                 </div>
                 <div className="flex-1 min-w-0">
                   <p className="text-indigo-400 text-[10px] font-black uppercase tracking-widest truncate">Enrolled Internship</p>
                   <h3 className="text-[15px] font-black text-slate-800 leading-tight truncate mt-0.5">{activeInternship?.offer?.internship_title}</h3>
                 </div>
                 <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg text-[10px] font-black uppercase tracking-wider px-2 py-1 flex-shrink-0">Active</Badge>
              </div>
            </div>
          ) : (
            <div className="bg-slate-50/80 backdrop-blur-md rounded-[1.5rem] p-4 border border-slate-200/60 border-dashed flex items-center gap-4">
              <div className="w-12 h-12 rounded-[1rem] bg-white flex items-center justify-center flex-shrink-0 shadow-sm border border-slate-100">
                <Sparkles className="text-slate-400 w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="text-slate-800 text-sm font-black leading-tight">No Active Internship</p>
                <p className="text-slate-500 text-[11px] leading-tight mt-1">Your next big opportunity is just a search away.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* --- DESKTOP HERO SECTION (Original Blue Container) --- */}
      <div className="hidden md:block relative group overflow-hidden rounded-[3rem] bg-gradient-to-br from-indigo-700 via-indigo-600 to-violet-800 p-1 pt-1.5 shadow-2xl shadow-indigo-200">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white/10 rounded-full blur-3xl transition-transform group-hover:scale-110 duration-700" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-indigo-400/20 rounded-full blur-3xl" />

        <div className="relative bg-indigo-600/10 rounded-[2.8rem] backdrop-blur-3xl p-10 flex flex-row justify-between items-center gap-10">
          <div className="space-y-6 text-left max-w-2xl">
            <div className="flex items-center justify-start gap-3">
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-none px-4 py-1.5 rounded-full text-xs font-black backdrop-blur-md uppercase tracking-wider">
                Student Portal
              </Badge>
              {activeInternship && (
                <Badge className="bg-emerald-400/20 text-emerald-300 border-none px-4 py-1.5 rounded-full text-xs font-black backdrop-blur-md uppercase tracking-wider flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Currently Interning
                </Badge>
              )}
            </div>

            <div className="space-y-3">
              <h1 className="text-6xl font-black text-white tracking-tight leading-tight">
                Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-white">{firstName}!</span>
              </h1>
              <p className="text-indigo-100/80 text-xl font-medium max-w-lg leading-relaxed">
                {activeInternship?.has_active_internship
                  ? `You are doing great as a ${activeInternship?.offer?.internship_title}. Keep up the high standards!`
                  : "Start exploring tailored internship opportunities and launch your career journey today."}
              </p>
            </div>

            <div className="flex flex-row items-center justify-start gap-4 pt-0">
              <Link to="/discover/internships">
                <Button className="bg-white text-indigo-700 hover:bg-indigo-50 px-8 py-7 rounded-[1.5rem] font-black shadow-xl hover:scale-105 transition-all text-base group">
                  {activeInternship?.has_active_internship ? "Explore Others" : "Find My Future"}
                  <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" className="text-white hover:bg-white/10 px-8 py-7 rounded-[1.5rem] font-bold text-base">
                  View Profile
                </Button>
              </Link>
            </div>
          </div>

          <div className="w-[400px]">
            {loadingActiveInternship ? (
              <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8 flex flex-col items-center justify-center space-y-4 h-[240px]">
                <Spinner className="text-white" />
                <p className="text-indigo-100/50 font-bold text-sm">Syncing career logs...</p>
              </div>
            ) : activeInternship?.has_active_internship ? (
              <div className="relative group/card bg-white/10 backdrop-blur-xl border border-white/20 rounded-[2.5rem] p-8 shadow-2xl transition-all hover:bg-white/15 overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/5 rounded-full blur-xl group-hover/card:scale-125 transition-transform" />
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-start">
                    <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-2xl shadow-indigo-900/20">
                      <Briefcase className="text-indigo-600" size={24} />
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-none rounded-lg text-[10px] font-black uppercase tracking-wider">Active Engagement</Badge>
                  </div>

                  <div className="space-y-1">
                    <p className="text-indigo-200 text-[10px] font-black uppercase tracking-[0.2em] opacity-70">Enrolled Internship</p>
                    <h3 className="text-2xl font-black text-white leading-tight tracking-tight">{activeInternship?.offer?.internship_title}</h3>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="bg-white/10 rounded-xl px-4 py-2 flex items-center gap-2">
                      <MapPin size={12} className="text-indigo-300" />
                      <span className="text-xs font-bold text-white">Remote</span>
                    </div>
                    <div className="bg-white/10 rounded-xl px-4 py-2 flex items-center gap-2">
                      <Clock size={12} className="text-indigo-300" />
                      <span className="text-xs font-bold text-white">12 Weeks</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-md border border-white/10 border-dashed rounded-[2.5rem] p-8 flex flex-col items-center justify-center text-center space-y-4 h-[240px]">
                <div className="w-16 h-16 rounded-3xl bg-white/10 flex items-center justify-center text-white/40">
                  <Sparkles size={32} />
                </div>
                <div className="space-y-1">
                  <p className="text-white text-base font-black">No Active Internship</p>
                  <p className="text-indigo-100/40 text-xs px-4">Your next big opportunity is just a search away.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-5 duration-700 delay-150">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
            <Sparkles className="text-indigo-500" size={24} />
            Quick Overview
          </h2>
          <Button variant="ghost" className="text-indigo-600 font-bold hover:bg-indigo-50">
            View Reports <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        <MetricsGrid className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {metrics.map((metric) => (
            <Link
              key={metric.testId}
              to={metric.href}
              className="block transition-transform hover:scale-[1.02]"
            >
              <MetricCard
                title={metric.title}
                value={metric.value}
                iconRight={metric.iconRight}
                trend={metric.trend as any}
                valueDescription={metric.valueDescription}
                testId={metric.testId}
                className={cn("h-full rounded-[2rem] border-none", metric.className)}
              />
            </Link>
          ))}
        </MetricsGrid>
      </div>

      {/* Featured Internships Section */}
      <div className="space-y-8 animate-in fade-in slide-in-from-bottom-5 duration-700 delay-200">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
            <LayoutGrid className="text-indigo-500" size={24} />
            Featured Opportunities
          </h2>
          <Link to="/discover/internships">
            <Button variant="ghost" className="text-indigo-600 font-bold hover:bg-indigo-50">
              Explore All <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        {loadingInternships ? (
          <div className="flex items-center justify-center py-20 bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/40">
            <Spinner className="text-indigo-600" />
          </div>
        ) : internships.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {internships.map((job: any) => (
              <FeaturedInternshipCard key={job.id || job._id} job={job} />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-dashed border-slate-200">
            <p className="text-slate-400 font-medium">No new opportunities at the moment. Check back soon!</p>
          </div>
        )}
      </div>

      {/* Active Internship Details Section */}
      {activeInternship?.has_active_internship && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-5 duration-700 delay-300">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <Briefcase className="text-indigo-500" size={24} />
              Internship Details
            </h2>
            <Link to="/work-logs">
              <Button variant="ghost" className="text-indigo-600 font-bold hover:bg-indigo-50">
                Manage Work Logs <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-white/40 rounded-[2.5rem] p-8 shadow-xl shadow-slate-200/40 space-y-8">
              <div className="flex flex-col md:flex-row justify-between gap-6">
                <div className="space-y-2">
                  <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em]">Current Position</p>
                  <h3 className="text-3xl font-black text-slate-800">{activeInternship?.offer?.internship_title}</h3>
                  <p className="text-slate-500 font-medium">{activeInternship?.offer?.company_name || "Enterprise Partner"}</p>
                </div>
                <div className="flex gap-2">
                  <Badge className="bg-emerald-50 text-emerald-600 border-none px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider">
                    Active
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-slate-100">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Starts</p>
                  <p className="text-sm font-bold text-slate-700">
                    {activeInternship?.offer?.start_date
                      ? formatDate(activeInternship.offer.start_date, { timeZone: "UTC" })
                      : "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Ends</p>
                  <p className="text-sm font-bold text-slate-700">
                    {activeInternship?.offer?.end_date
                      ? formatDate(activeInternship.offer.end_date, { timeZone: "UTC" })
                      : "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Stipend</p>
                  <p className="text-sm font-bold text-slate-700">
                    {activeInternship?.offer?.stipend
                      ? `₹${activeInternship.offer.stipend.toLocaleString()}/mo`
                      : "Unpaid"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</p>
                  <p className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 size={14} /> Confirmed
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-indigo-200 flex flex-col justify-between overflow-hidden relative group">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
              <div className="relative space-y-2">
                <h4 className="text-xl font-black">Daily Tracking</h4>
                <p className="text-indigo-100/70 text-sm font-medium">Keep your progress updated to stay compliant with your internship requirements.</p>
              </div>
              <div className="relative pt-8">
                <Link to="/work-logs">
                  <Button className="w-full bg-white text-indigo-600 hover:bg-indigo-50 rounded-2xl h-14 font-black shadow-lg">
                    Go to Work Logs
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeaturedInternshipCard = ({ job }: { job: any }) => {
  const id = job.id || job._id;
  const title = job.title || "Untitled Position";
  const company = job.company_name || "Enterprise Partner";
  const location = job.location || (job.is_remote ? "Remote" : "Hybrid");

  return (
    <Link to={`/discover/internships/${id}`} className="block group">
      <div className="relative h-full bg-white/60 backdrop-blur-xl border border-white/40 rounded-[2rem] p-6 shadow-xl shadow-slate-200/40 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-indigo-200/50 hover:bg-white/80 overflow-hidden">
        {/* Decorative background glow */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/5 rounded-full blur-3xl group-hover:bg-indigo-500/10 transition-colors" />

        <div className="relative flex flex-col h-full gap-4">
          <div className="flex justify-between items-start">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 to-violet-50 flex items-center justify-center shadow-inner group-hover:from-indigo-100 group-hover:to-violet-100 transition-colors">
              <Building2 className="text-indigo-600" size={24} />
            </div>
            {job.is_remote && (
              <Badge className="bg-indigo-50 text-indigo-600 border-none px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
                Remote
              </Badge>
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-lg font-black text-slate-800 line-clamp-1 group-hover:text-indigo-600 transition-colors">{title}</h3>
            <p className="text-sm font-bold text-slate-500">{company}</p>
          </div>

          <div className="flex flex-wrap gap-3 mt-auto">
            <div className="flex items-center gap-1.5 text-slate-400">
              <MapPin size={14} className="text-indigo-400" />
              <span className="text-xs font-bold">{location}</span>
            </div>
            {job.stipend_min && (
              <div className="flex items-center gap-1.5 text-emerald-600">
                <IndianRupee size={14} />
                <span className="text-xs font-black">{job.stipend_min.toLocaleString()}</span>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-100/50 flex items-center justify-between">
            <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{job.domain || "Technology"}</span>
            <div className="flex items-center gap-1 text-indigo-600 font-black text-xs opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all">
              View Details <ChevronRight size={14} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Dashboard;
